import React, { useState, useEffect } from 'react'
import { api } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import {
  UserCircleIcon,
  CheckBadgeIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { CATEGORY_VALUE_TO_LABEL } from '../../constants/product-categories'
import { ADDRESS_STATE_NAMES, getCitiesForStateName } from '../../constants/malaysian-states'

interface VendorProfile {
  id: string
  companyName: string
  email: string
  phone?: string
  address?: string
  city?: string
  state?: string
  postalCode?: string
  description?: string
  categories: string[]
  isVerified: boolean
  isActive: boolean
  totalProducts: number
  totalOrders: number
  totalRevenue: number
}

interface AddressFormData {
  address: string
  city: string
  state: string
  postalCode: string
}

const VendorProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<VendorProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const [editSuccess, setEditSuccess] = useState(false)
  const [addressData, setAddressData] = useState<AddressFormData>({
    address: '',
    city: '',
    state: '',
    postalCode: '',
  })

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      setLoading(true)
      const response = await api.get('/souvenir-vendors/my-profile')
      setProfile(response.data)
      setAddressData({
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        postalCode: response.data.postalCode || '',
      })
    } catch (err: any) {
      console.error('Error fetching profile:', err)
      setError(err.response?.data?.message || 'Failed to fetch profile')
    } finally {
      setLoading(false)
    }
  }

  const startEdit = () => {
    setEditing(true)
    setEditError(null)
    setEditSuccess(false)
    setAddressData({
      address: profile?.address || '',
      city: profile?.city || '',
      state: profile?.state || '',
      postalCode: profile?.postalCode || '',
    })
  }

  const cancelEdit = () => {
    setEditing(false)
    setEditError(null)
  }

  const handleAddressChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setAddressData((prev) => ({ ...prev, [name]: value }))
  }

  const saveAddress = async () => {
    setSaving(true)
    setEditError(null)
    setEditSuccess(false)
    try {
      await api.put('/souvenir-vendors/my-profile', {
        address: addressData.address.trim() || undefined,
        city: addressData.city.trim() || undefined,
        state: addressData.state.trim() || undefined,
        postalCode: addressData.postalCode.trim() || undefined,
      })
      // PUT returns {success, data} without stats — re-fetch the full profile
      await fetchProfile()
      setEditing(false)
      setEditSuccess(true)
      setTimeout(() => setEditSuccess(false), 3000)
    } catch (err: any) {
      console.error('Error updating vendor profile:', err)
      setEditError(err.response?.data?.message || 'Failed to update address')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        Error: {error}
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
        Vendor profile not found
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Vendor Profile</h1>
        <p className="text-gray-600 mt-1">Manage your vendor information</p>
      </div>

      {editSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          Address updated successfully.
        </div>
      )}

      {/* Profile Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Company Information</h3>
          {!editing ? (
            <button
              onClick={startEdit}
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
            >
              <PencilIcon className="h-4 w-4" />
              Edit Address
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={saveAddress}
                disabled={saving}
                className="px-3 py-1.5 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={cancelEdit}
                className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50"
              >
                <XMarkIcon className="h-4 w-4" />
                Cancel
              </button>
            </div>
          )}
        </div>
        <div className="p-6 space-y-6">
          {/* Company Name & Verification */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xl font-semibold text-gray-900">{profile.companyName}</h4>
              <div className="flex items-center gap-4 mt-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  profile.isVerified
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {profile.isVerified ? 'Verified' : 'Pending Verification'}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  profile.isActive
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {profile.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            {profile.isVerified && (
              <CheckBadgeIcon className="h-8 w-8 text-green-500" />
            )}
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Email</label>
              <p className="mt-1 text-sm text-gray-900">{profile.email}</p>
            </div>
            {profile.phone && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Phone</label>
                <p className="mt-1 text-sm text-gray-900">{profile.phone}</p>
              </div>
            )}
          </div>

          {/* Address Section */}
          {editError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm">
              {editError}
            </div>
          )}

          {!editing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Street Address</label>
                <p className="mt-1 text-sm text-gray-900">{profile.address || 'Not provided'}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500">City</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.city || 'Not provided'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">State</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.state || 'Not provided'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Postal Code</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.postalCode || 'Not provided'}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Street Address</label>
                <textarea
                  name="address"
                  value={addressData.address}
                  onChange={handleAddressChange}
                  rows={2}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">City</label>
                  <input
                    type="text"
                    name="city"
                    value={addressData.city}
                    onChange={handleAddressChange}
                    list="vendor-city-options"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
                  />
                  <datalist id="vendor-city-options">
                    {getCitiesForStateName(addressData.state).map((city) => (
                      <option key={city} value={city} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">State</label>
                  <select
                    name="state"
                    value={addressData.state}
                    onChange={handleAddressChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
                  >
                    <option value="">Select State</option>
                    {ADDRESS_STATE_NAMES.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                  <input
                    type="text"
                    name="postalCode"
                    value={addressData.postalCode}
                    onChange={handleAddressChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-4 py-2 border"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          {profile.description && (
            <div>
              <label className="block text-sm font-medium text-gray-500">Description</label>
              <p className="mt-1 text-sm text-gray-900">{profile.description}</p>
            </div>
          )}

          {/* Categories */}
          <div>
            <label className="block text-sm font-medium text-gray-500">Product Categories</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {profile.categories.map((category) => (
                <span
                  key={category}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                >
                  {CATEGORY_VALUE_TO_LABEL[category] || category}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Performance Stats</h3>
        </div>
        <div className="p-6">
          <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="bg-blue-50 overflow-hidden rounded-lg px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-blue-500 truncate">Total Products</dt>
              <dd className="mt-1 text-3xl font-semibold text-blue-900">{profile.totalProducts}</dd>
            </div>
            <div className="bg-green-50 overflow-hidden rounded-lg px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-green-500 truncate">Total Orders</dt>
              <dd className="mt-1 text-3xl font-semibold text-green-900">{profile.totalOrders}</dd>
            </div>
            <div className="bg-purple-50 overflow-hidden rounded-lg px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-purple-500 truncate">Total Revenue</dt>
              <dd className="mt-1 text-3xl font-semibold text-purple-900">
                RM{Number(profile.totalRevenue || 0).toFixed(2)}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {!profile.isVerified && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Account Pending Verification
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Your vendor account is currently pending verification. You will be able to manage products and receive orders once your account is verified by the admin.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VendorProfilePage
